import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

import ReactECharts from 'echarts-for-react';
import { MS_CONFIGS } from '../../../organisms/SecufireActionLogs/SecufireActionLogs';
import './DonutChart.css';

const XS_WIDTH = 360;
const MOBILE_WIDTH = 520;
const COMPACT_WIDTH = 760;

const useContainerWidth = (ref) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let frame = null;

    const observer = new ResizeObserver((entries) => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const entry = entries[entries.length - 1];
        const newWidth = entry?.contentRect?.width ?? element.getBoundingClientRect().width;
        setWidth((prev) => (prev === newWidth ? prev : newWidth));
      });
    });

    observer.observe(element);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [ref]);

  return width;
};

const ENDPOINT_PALETTE = [
  '#0EA5E9', '#0C4A6E', '#8B5CF6', '#6366F1', '#0891B2',
  '#F59E0B', '#0369A1', '#EF4444', '#4338CA', '#84CC16',
];

const getEndpointColor = (index) =>
  ENDPOINT_PALETTE[index % ENDPOINT_PALETTE.length];

const DonutChart = forwardRef(
  (
    {
      mode = 'microservice',
      microservices = [],
      selectedMsSet,
      endpoints = [],
      totalCalls,
      color = '#0EA5E9',
    },
    ref
  ) => {
    const containerRef = useRef(null);
    const chartRef = useRef(null);

    const containerWidth = useContainerWidth(containerRef);

    /*
     * ── FIX : le texte central (.donut-chart__center) est un <div>
     * HTML superposé par CSS, pas un élément dessiné par ECharts —
     * getDataURL() (qui ne rend que le canvas ECharts) ne le capture
     * donc jamais. getNode() expose le conteneur DOM COMPLET (canvas +
     * texte central), à utiliser avec exportNodeAsPNG côté parent pour
     * que le PNG exporté contienne bien le total affiché au centre.
     * getDataURL reste disponible pour les cas qui n'en ont pas besoin.
     */
    useImperativeHandle(ref, () => ({
      getDataURL: (opts) =>
        chartRef.current
          ?.getEchartsInstance?.()
          ?.getDataURL(opts),
      getNode: () => containerRef.current,
    }));

    const layout =
      containerWidth <= XS_WIDTH
        ? 'xs'
        : containerWidth <= MOBILE_WIDTH
        ? 'mobile'
        : containerWidth <= COMPACT_WIDTH
        ? 'compact'
        : 'desktop';

    const isBottomLegend =
      layout === 'xs' ||
      layout === 'mobile';

    const pieRadius =
      layout === 'xs'
        ? ['38%', '60%']
        : layout === 'mobile'
        ? ['40%', '64%']
        : layout === 'compact'
        ? ['40%', '64%']
        : ['42%', '68%'];

    const pieCenter =
      layout === 'xs'
        ? ['50%', '40%']
        : layout === 'mobile'
        ? ['50%', '40%']
        : layout === 'compact'
        ? ['30%', '50%']
        : ['30%', '50%'];

    const centerStyle = {
      left: pieCenter[0],
      top: pieCenter[1],
    };

    const sideLegendWidthPx = Math.max(
      70,
      Math.round(containerWidth * (layout === 'compact' ? 0.46 : 0.48))
    );

    const legendProps = isBottomLegend
      ? {
          orient: 'horizontal',
          left: 'center',
          right: 'auto',
          top: 'auto',
          bottom: 2,
          width: '94%',
          type: 'scroll',
          padding: [8, 4, 4, 4],
          textStyle: {
            fontSize: layout === 'xs' ? 8 : 9,
            color: '#475569',
            fontWeight: 500,
            width: Math.max(60, Math.round(containerWidth * 0.4)),
            overflow: 'break',
            lineHeight: layout === 'xs' ? 10 : 11,
          },
          itemWidth: layout === 'xs' ? 7 : 8,
          itemHeight: layout === 'xs' ? 7 : 8,
          itemGap: 8,
        }
      : {
          orient: 'vertical',
          left: layout === 'compact' ? '52%' : '50%',
          right: '3%',
          top: 'middle',
          bottom: 'auto',
          type: 'scroll',
          padding: [10, 6, 10, 6],
          textStyle: {
            fontSize: layout === 'compact' ? 9.5 : 10.5,
            color: '#475569',
            fontWeight: 500,
            width: sideLegendWidthPx - 26,
            overflow: 'break',
            lineHeight: layout === 'compact' ? 12 : 13,
          },
          itemWidth: layout === 'compact' ? 8 : 9,
          itemHeight: layout === 'compact' ? 8 : 9,
          itemGap: layout === 'compact' ? 10 : 12,
        };

    const buildOption = ({ allData, total, seriesData }) => ({
      animation: true,
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(255,255,255,0.97)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        textStyle: { color: '#1e293b', fontSize: 11 },
        formatter: (p) => {
          const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0;
          return `
            <div style="font-weight:700;margin-bottom:4px">${p.name}</div>
            <div style="display:flex;align-items:center;gap:8px">
              <span style="color:#64748b">${p.value.toLocaleString()} appels</span>
              <strong style="margin-left:auto;color:${p.color}">${pct}%</strong>
            </div>
          `;
        },
      },
      legend: {
        icon: 'circle',
        formatter: (name) => {
          const item = allData.find((entry) => entry.name === name);
          const pct = item && total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
          return `${name}  ${pct}%`;
        },
        ...legendProps,
      },
      series: [
        {
          type: 'pie',
          center: pieCenter,
          radius: pieRadius,
          avoidLabelOverlap: true,
          label: { show: false },
          labelLine: { show: false },
          emphasis: { scale: true, scaleSize: 5, label: { show: false } },
          data: seriesData,
        },
      ],
    });

    const msMemo = useMemo(() => {
      if (mode !== 'microservice') return null;
      const allMs = microservices.filter((m) => (m.total_usage || 0) > 0);
      const total = allMs.reduce((sum, m) => sum + (m.total_usage || 0), 0);
      if (!allMs.length) return null;

      const seriesData = allMs.map((m) => ({
        name: MS_CONFIGS[m.microservice]?.label || m.microservice,
        value: m.total_usage || 0,
        itemStyle: {
          color: MS_CONFIGS[m.microservice]?.color || '#64748b',
          opacity: selectedMsSet?.has(m.microservice) ? 1 : 0.22,
        },
      }));

      const allData = seriesData.map((item) => ({ name: item.name, value: item.value }));

      return { total, option: buildOption({ allData, total, seriesData }) };
    }, [mode, microservices, selectedMsSet, layout, containerWidth]);

    const epMemo = useMemo(() => {
      if (mode !== 'endpoint') return null;
      const allEp = endpoints.filter((e) => (e.total_usage || 0) > 0);
      const total = allEp.reduce((sum, e) => sum + (e.total_usage || 0), 0);
      if (!allEp.length) return null;

      const seriesData = allEp.map((e, i) => ({
        name: e.endpoint || e.functionality || `ep${i}`,
        value: e.total_usage || 0,
        itemStyle: { color: getEndpointColor(i) },
      }));

      const allData = seriesData.map((item) => ({ name: item.name, value: item.value }));

      return { total, option: buildOption({ allData, total, seriesData }) };
    }, [mode, endpoints, layout, containerWidth]);

    const memo = mode === 'endpoint' ? epMemo : msMemo;

    if (!memo) {
      return (
        <div ref={containerRef} className="donut-chart donut-chart--empty">
          <span className="donut-chart__empty-text">Aucune donnée</span>
        </div>
      );
    }

    const showCenterText = true;
    const showTotalLabel = true;

    const centerFontSize =
      layout === 'desktop'
        ? 20
        : layout === 'compact'
        ? 17
        : layout === 'mobile'
        ? 15
        : 12;

    const hasMeasuredWidth = containerWidth > 0;

    return (
      <div
        ref={containerRef}
        className={`donut-chart donut-chart--${layout}`}
      >
        {hasMeasuredWidth && (
          <>
            <ReactECharts
              ref={chartRef}
              option={memo.option}
              style={{ width: '100%', height: '100%' }}
              notMerge={true}
              lazyUpdate={true}
              opts={{ renderer: 'canvas' }}
            />

            {showCenterText && (
              <div
                className="donut-chart__center"
                style={{ left: centerStyle.left, top: centerStyle.top }}
              >
                <span className="donut-chart__total" style={{ fontSize: centerFontSize }}>
                  {memo.total.toLocaleString()}
                </span>

                {showTotalLabel && (
                  <span className="donut-chart__label">appels total</span>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);

DonutChart.displayName = 'DonutChart';

export default DonutChart;